/**
 * Optimized lodash utilities - only methods without native alternatives.
 *
 * Usage: import { debounce, round } from '@/libs/lodash'
 *
 * Native replacements (use these instead):
 *   cloneDeep → deepClone(obj) (handles Vue 3 reactive proxies)
 *   concat → array.concat()
 *   flatten → array.flat()
 *   flatMap → array.flatMap()
 *   get → optional chaining (obj?.path?.to?.value)
 *   isInteger → Number.isInteger()
 *   remove → array.filter()
 *   sum → array.reduce((a, b) => a + b, 0)
 *   values → Object.values()
 */


import {cloneDeep} from "lodash";
// Deep clone that safely handles Vue 3 reactive proxies
import {isReactive, isRef, Reactive, Ref, toRaw} from 'vue'

export function deepClone<T>(source: T|Ref<T>|Reactive<T>): T {
  const raw = isRef(source) ? toRaw((source as Ref<T>).value) : toRaw(source as unknown)
  try {
    if (isReactive(source) || isRef(source)) {
      return JSON.parse(JSON.stringify(raw)) as T
    }
    return structuredClone(raw as T)
  } catch {
    return cloneDeep(raw as T)
  }
}

// Function utilities - no native debounce
export { debounce } from 'lodash'

// Math - round keeps precision, sumBy adds convenience
export { round, sumBy, sum } from 'lodash'

// Deep merge - no native equivalent
export { merge } from 'lodash'

// Complex array sorting
export { orderBy, shuffle, sortBy, groupBy, difference, uniqBy, remove } from 'lodash'

// String case conversion - no native alternatives
export { camelCase, snakeCase, upperFirst } from 'lodash'

// Deep equality check - no native deep comparison
export { isEqual, isNumber, isString, xor } from 'lodash'

export function isNull(value: unknown): boolean {
  return value === null
}
export function uniq<T>(array: T[]): T[] {
  return [...new Set(array)]
}
